import React, { createContext, useContext, useEffect, useState } from 'react';

type Density = 'compact' | 'large';

type DensityProviderState = {
  density: Density;
  setDensity: (density: Density) => void;
};

const STORAGE_KEY = 'localagent-density';
const DEFAULT_DENSITY: Density = 'compact';

const FONT_SIZES: Record<Density, string> = {
  compact: '14px',
  large: '16px',
};

const initialState: DensityProviderState = {
  density: DEFAULT_DENSITY,
  setDensity: () => null,
};

const DensityProviderContext = createContext<DensityProviderState>(initialState);

export function DensityProvider({ children }: { children: React.ReactNode }) {
  const [density, setDensityState] = useState<Density>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return (stored === 'compact' || stored === 'large') ? stored : DEFAULT_DENSITY;
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.setAttribute('data-density', density);
    root.style.fontSize = FONT_SIZES[density];
  }, [density]);

  const setDensity = (d: Density) => {
    localStorage.setItem(STORAGE_KEY, d);
    setDensityState(d);
  };

  return (
    <DensityProviderContext.Provider value={{ density, setDensity }}>
      {children}
    </DensityProviderContext.Provider>
  );
}

export const useDensity = () => {
  const context = useContext(DensityProviderContext);
  if (context === undefined) throw new Error('useDensity must be used within a DensityProvider');
  return context;
};
